import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Trash2, GraduationCap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ProjectCardProps {
  project: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    created_at: string;
  };
  onUpdate: () => void;
}

const ProjectCard = ({ project, onUpdate }: ProjectCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm("Are you sure you want to delete this course?")) return;

    const { error } = await supabase
      .from("learning_projects")
      .delete()
      .eq("id", project.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error deleting course",
        description: error.message,
      });
    } else {
      toast({
        title: "Success",
        description: "Course deleted successfully",
      });
      onUpdate();
    }
  };

  const handleLearnClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/learn/courses/${project.id}`);
  };

  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => navigate(`/project/${project.id}`)}
    >
      <CardHeader>
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <CardTitle className="line-clamp-1">{project.title}</CardTitle>
            <CardDescription className="line-clamp-2 mt-1">
              {project.description || "No description"}
            </CardDescription>
          </div>
          <Badge variant="secondary" className="ml-2">
            {project.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleLearnClick}
              className="text-primary"
            >
              <GraduationCap className="mr-1 h-4 w-4" />
              Learn
            </Button>
            <div className="flex items-center text-sm text-muted-foreground">
              <FileText className="mr-1 h-4 w-4" />
              <span>Manage</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDelete}
            className="text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
